/**
 * TanStack Query hook for `GET /api/admin/finance-upload-history`.
 *
 * Returns the last 12 upload-log rows (newest first) so the SPA history
 * table on `/admin/finance-upload` can render the "last upload" status box
 * + 12-month history per UI-SPEC.
 *
 * Config:
 *   - queryKey: ['finance', 'upload-history']
 *   - staleTime: 60s — Finance uploads at most monthly; fresh-on-every-render
 *     would be overkill.
 *   - retry: 1 — transient server errors get one retry, then the SPA shows
 *     "Could not load history" inline.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  apiFetch,
  type ApiClientError,
  type ApiServerError,
  type UnauthenticatedError,
} from '../lib/api-client.js';

import type { FinanceUploadHistoryRow } from '@saledash/shared';

type HistoryError = ApiServerError | ApiClientError | UnauthenticatedError;

export function useFinanceUploadHistory(): UseQueryResult<FinanceUploadHistoryRow[], HistoryError> {
  return useQuery<FinanceUploadHistoryRow[], HistoryError>({
    queryKey: ['finance', 'upload-history'],
    queryFn: ({ signal }) =>
      apiFetch<FinanceUploadHistoryRow[]>('/api/admin/finance-upload-history', { signal }),
    staleTime: 60 * 1000,
    retry: 1,
  });
}
